import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Settings2, X } from "lucide-react";

const SCOPE_STYLE = { system: "bg-slate-100 text-slate-500", account: "bg-sky-50 text-sky-700", group: "bg-teal-50 text-teal-700" };

export function ScopeTag({ s }) {
  return <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${SCOPE_STYLE[s.scope] || ""}`}>{s.scope === "group" ? s.owner_name : s.scope}</span>;
}

/** Searchable source picker resolved for one destination organization. Value is a stable source id. */
export function SourceSelect({ organizationId, value, label, onChange, onUnavailable, testId = "source-select", className = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const saved = useRef({ id: value, label });

  const load = useCallback(() => {
    api.get("/sources", { params: { selectable_only: true, organization_id: organizationId || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [organizationId]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const current = items.find((s) => s.id === value);
  const isSaved = value && value === saved.current.id;

  useEffect(() => {
    if (data && value && !current && !isSaved && onUnavailable) onUnavailable(label);
  }, [data, value, current, isSaved, label, onUnavailable]);

  const needsDest = data?.needs_destination;
  const text = current ? current.label : value || label ? `${label || "Saved source"}${isSaved || !value ? " (inactive or not available here)" : ""}` : needsDest ? "Choose a destination first" : "Select a source";
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={needsDest} data-testid={testId} aria-label="Source"
          className={`w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-left text-sm flex items-center justify-between gap-2 hover:border-[#006090]/50 focus:outline-none focus:ring-2 focus:ring-[#006090]/30 disabled:opacity-60 transition-colors ${className}`}>
          <span className={`truncate ${current ? "text-slate-800" : value || label ? "text-amber-700" : "text-slate-400"}`} data-testid={`${testId}-value`}>{text}</span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px] bg-white" align="start">
        <Command filter={(v, s) => (v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Search sources…" data-testid={`${testId}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>No matching source.</CommandEmpty>
            <CommandGroup>
              {(value || label) && (
                <CommandItem value="__clear__ none clear" onSelect={() => { setOpen(false); onChange({ source_id: "", source: "" }); }} data-testid={`${testId}-clear`} className="text-slate-500">
                  <X className="w-4 h-4 mr-2" />No source
                </CommandItem>
              )}
              {items.map((s) => (
                <CommandItem key={s.id} value={`${s.label} ${s.scope} ${s.owner_name} ${s.id}`} onSelect={() => { setOpen(false); onChange({ source_id: s.id, source: s.label }); }}
                  data-testid={`${testId}-opt-${s.id}`}>
                  <Check className={`w-4 h-4 mr-2 shrink-0 ${s.id === value ? "opacity-100 text-[#006090]" : "opacity-0"}`} />
                  <span className="flex-1 truncate text-sm">{s.label}</span>
                  <ScopeTag s={s} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {data?.can_manage && (
            <Link to="/admin/statuses-sources" onClick={() => setOpen(false)} data-testid={`${testId}-manage`}
              className="flex items-center gap-1.5 border-t border-slate-100 px-3 py-2 text-xs font-medium text-[#006090] hover:bg-slate-50">
              <Settings2 className="w-3.5 h-3.5" />Manage Sources
            </Link>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
